import IconButton from "@/components/atoms/IconButton";
import AuthCardLayout from "@/components/molecules/AuthCardLayout";
import { PASSWORD_REGEX } from "@/constants/regexs";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { matchPassword } from "@/helpers/FormHelpers";
import { IResetPasswordForm } from "@/interfaces/ForgotPassword";
import { useResetPasswordMutation } from "@/services/auth";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const ResetForgotPasswordView: FC = () => {
  const { email } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IResetPasswordForm>();
  const [resetPasswordAsync] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState<boolean>(false);
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<IResetPasswordForm> = async (data) => {
    await toast.promise(resetPasswordAsync(data).unwrap(), {
      pending: "Réinitialisation en cours...",
      success: {
        render() {
          navigate("/auth/login");
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              Votre mot de passe à bien été modifié 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Votre mot de passe ou votre adresse e-mail est incorrecte.";
          } else if (data?.status === 403) {
            return "Le code de vérification est invalide ou a expiré";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<IResetPasswordForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <AuthCardLayout
      leftSideContent={
        <p className="text-white">
          Vous n&apos;avez pas de compte ?
          <br />
          <Link
            className="text-white text-decoration-underline"
            to="/auth/register"
          >
            Faire une demande
          </Link>
        </p>
      }
    >
      <h3>Mot de passe oublié</h3>
      <Form className="mt-4" onSubmit={handleSubmit(onSubmit, onError)}>
        <Row className="gx-3">
          <Col lg={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Code OTP à 6 chiffres <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                placeholder="ex : 545644"
                type="text"
                {...register("otp", {
                  required: "Le code OTP est obligatoire",
                })}
                isInvalid={!!errors.otp}
              />
              <Form.Control.Feedback type="invalid">
                {errors.otp?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                placeholder="ex : 8ddn2kKDB"
                type="email"
                {...register("email", {
                  required: "L'email est obligatoire",
                })}
                defaultValue={email}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={12}>
            <div className="mb-3">
              <Form.Label>
                Mot de passe
                <span className="text-danger"> *</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  placeholder="ex : 8ddn2kKDB"
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: "Le nouveau mot de passe est obligatoire",
                    pattern: {
                      value: PASSWORD_REGEX,
                      message:
                        "Votre mot de passe doit contenir au moins 8 caractères, une lettre majuscule, une lettre minuscule, un chiffre et un caractère spécial",
                    },
                  })}
                  isInvalid={!!errors.password}
                  className="form-control"
                />
                <InputGroup.Text
                  as={IconButton}
                  variant="outline-secondary"
                  icon={showPassword ? faEyeSlash : faEye}
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle-button"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </div>
          </Col>
          <Col lg={12}>
            <div className="mb-3">
              <Form.Label>
                Confirmation du nouveau mot de passe
                <span className="text-danger"> *</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  placeholder="ex : 9rfDJde7d"
                  type={showPasswordConfirmation ? "text" : "password"}
                  {...register("passwordConfirmation", {
                    required: "La confirmation du mot de passe est obligatoire",
                    validate: (value) =>
                      matchPassword(value, watch("password")) ||
                      "Les mots de passe ne correspondent pas",
                  })}
                  isInvalid={!!errors.passwordConfirmation}
                  className="form-control"
                />
                <InputGroup.Text
                  as={IconButton}
                  variant="outline-secondary"
                  onClick={() =>
                    setShowPasswordConfirmation(!showPasswordConfirmation)
                  }
                  className="password-toggle-button"
                  icon={showPasswordConfirmation ? faEyeSlash : faEye}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.passwordConfirmation?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </div>
          </Col>
        </Row>

        <Button className="w-100 mb-3" type="submit">
          Réinitialiser le mot de passe
        </Button>

        <Link className="fs--1 text-600" to="/auth/login">
          Retour à la page de connexion
          <span className="d-inline-block ms-1"> &rarr;</span>
        </Link>
      </Form>
    </AuthCardLayout>
  );
};

export default ResetForgotPasswordView;
